﻿import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GameOverlays } from '@replay/shared';
import { api, ApiError } from '../../lib/api.ts';
function read<T>(key: string): T | undefined {
  try {
    const v = localStorage.getItem(key);
    return v === null ? undefined : (JSON.parse(v) as T);
  } catch {
    return undefined;
  }
}
function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Storage may be unavailable. */
  }
}
export function useOverlayChoice(gameId: string) {
  const qc = useQueryClient(),
    key = `replay.overlay.${gameId}`,
    queryKey = ['overlays', gameId];
  const [portrait, setPortrait] = useState(() => matchMedia('(orientation: portrait)').matches);
  useEffect(() => {
    const m = matchMedia('(orientation: portrait)'),
      fn = () => setPortrait(m.matches);
    fn();
    m.addEventListener('change', fn);
    return () => m.removeEventListener('change', fn);
  }, []);
  const query = useQuery<GameOverlays>({
    queryKey,
    queryFn: async ({ signal }) => {
      const data = await api<GameOverlays>(`/api/games/${gameId}/overlays`, { signal });
      const pending = read<{ choice: string | null }>(`${key}.pending`);
      if (pending) {
        try {
          await api(`/api/games/${gameId}/overlay-choice`, { method: 'PUT', body: pending, signal });
          data.choice = pending.choice;
        } catch (e) {
          if (!(e instanceof ApiError) || !['validation', 'not_found'].includes(e.kind)) throw e;
          // A removed/disabled overlay can no longer be restored after reconnecting.
        }
        try {
          localStorage.removeItem(`${key}.pending`);
        } catch {}
      }
      write(`${key}.response`, data);
      write(key, data.choice);
      return data;
    },
    initialData: () => {
      const data = read<GameOverlays>(`${key}.response`);
      if (data) {
        const choice = read<string | null>(key);
        return { ...data, choice: choice === undefined ? data.choice : choice };
      }
      return undefined;
    },
    retry: false,
  });
  const mutation = useMutation({
    networkMode: 'always',
    mutationFn: async (choice: string | null) => {
      try {
        await api(`/api/games/${gameId}/overlay-choice`, { method: 'PUT', body: { choice } });
        try {
          localStorage.removeItem(`${key}.pending`);
        } catch {}
      } catch (e) {
        if (e instanceof ApiError && e.kind === 'network') {
          write(`${key}.pending`, { choice });
          return;
        }
        throw e;
      }
    },
    onMutate: async (choice) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<GameOverlays>(queryKey);
      const next = { overlays: previous?.overlays ?? [], choice };
      qc.setQueryData(queryKey, next);
      write(key, choice);
      write(`${key}.response`, next);
      return { previous };
    },
    onError: (_e, _choice, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous);
        write(key, context.previous.choice);
        write(`${key}.response`, context.previous);
      }
    },
  });
  const overlays = query.data?.overlays ?? [],
    choice = query.data?.choice ?? null;
  const selected = choice === null ? overlays.find((o) => o.isDefault) : overlays.find((o) => o.id === choice);
  const layout = selected
    ? ((portrait ? (selected.layouts.portrait ?? selected.layouts.landscape) : (selected.layouts.landscape ?? selected.layouts.portrait)) ?? null)
    : null;
  const mode: 'overlay' | 'minimal' | 'none' = choice === 'none' ? 'none' : choice === 'minimal' ? 'minimal' : layout ? 'overlay' : 'minimal';
  return { overlays, choice, setChoice: mutation.mutateAsync, layout, mode };
}
